
import React, { useState } from "react";
import { Grid, Button } from "@mui/material";
import { useInvestAssistantContext } from "./ContextType";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { haptic } from "./haptics";

type ButtonsProps = {
    forwardEnabled?: boolean;
    backEnabled?: boolean;
    forwardVisible?: boolean;
    backVisible?: boolean;
    labelForward?: string;
    labelBack?: string;
    onForward?: () => Promise<boolean>;
    increaseStepNumber?: boolean;
    decreaseStepNumber?: boolean;
    alignment?: 'flex-start' | 'flex-end';
};

function ButtonsComponent(props: ButtonsProps) {
    const { handlePrev, handleNext } = useInvestAssistantContext();
    const [loading, setLoading] = useState(false);

    const forwardVisible = props.forwardVisible ?? true;
    const backVisible = props.backVisible ?? true;
    const forwardEnabled = props.forwardEnabled ?? true;
    const backEnabled = props.backEnabled ?? true;
    const labelForward = props.labelForward ?? 'Los geht`s!';
    const labelBack = props.labelBack ?? 'Zurück';
    const increaseStepNumber = props.increaseStepNumber ?? true;
    const decreaseStepNumber = props.decreaseStepNumber ?? true;
    const onForward = props.onForward ?? (() => Promise.resolve(true));
    const alignment = props.alignment ?? 'flex-start';

    const handleForward = async () => {
        if (loading) return;
        haptic(12);
        setLoading(true);
        const result = await onForward();
        setLoading(false);
        if (result) {
            handleNext(increaseStepNumber);
        } else {
            // short double-buzz to signal an unsuccessful / invalid submit
            haptic([8, 40, 8]);
        }
    };

    const handleBack = () => {
        haptic(8);
        handlePrev(decreaseStepNumber);
    };

    return (
        <>
            {forwardVisible && (
                <Grid container justifyContent={alignment} paddingTop={3}>
                    <Button
                        variant="contained"
                        onClick={handleForward}
                        sx={{
                            textTransform: 'uppercase',
                            fontWeight: 'bold',
                            backgroundColor: '#262527',
                            color: '#ffffff',
                            transition: 'transform .12s ease, box-shadow .2s ease, opacity .2s ease',
                            willChange: 'transform',
                            '&:hover': {
                                backgroundColor: '#262527',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 6px 16px rgba(0, 0, 0, .18)',
                            },
                            '&:active': {
                                transform: 'translateY(0) scale(.97)',
                                boxShadow: '0 2px 6px rgba(0, 0, 0, .18)',
                            },
                            '&.Mui-disabled': {
                                opacity: .45,
                                color: '#ffffff',
                            },
                            '@media (prefers-reduced-motion: reduce)': {
                                transition: 'none',
                                '&:hover, &:active': { transform: 'none' },
                            },
                        }}
                        disabled={!forwardEnabled || loading}
                    >
                        {labelForward}
                    </Button>
                </Grid>
            )}
            {backVisible && (
                <Grid container justifyContent="flex-start" sx={{ mt: '40px' }}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                        sx={{
                            color: 'black',
                            border: 'unset',
                            transition: 'transform .12s ease, opacity .2s ease',
                            '&:hover': {
                                color: 'black',
                                border: 'unset',
                                transform: 'translateX(-2px)',
                            },
                            '&:active': {
                                transform: 'translateX(-2px) scale(.97)',
                            },
                            '& .MuiButton-startIcon': {
                                transition: 'transform .12s ease',
                            },
                            '&:hover .MuiButton-startIcon': {
                                transform: 'translateX(-2px)',
                            },
                            '@media (prefers-reduced-motion: reduce)': {
                                transition: 'none',
                                '&:hover, &:active': { transform: 'none' },
                                '&:hover .MuiButton-startIcon': { transform: 'none' },
                            },
                        }}
                        disabled={!backEnabled}
                    >
                        {labelBack}
                    </Button>
                </Grid>
            )}
        </>
    );
}

export const FooterButtons = (props: ButtonsProps) => (
    <ButtonsComponent {...props} alignment="flex-start" />
);

export const Buttons = (props: ButtonsProps) => (
    <ButtonsComponent {...props} alignment="flex-end" />
);